import { entityMasterSingleEditFactory } from 'common/entity/generic/SingleEntityEditContainer';
import { MasterLineItem } from 'common/models/internal/MasterLineItem';
import { FilterBuilder, FilterOperation } from 'common/models/internal/Filter';
import { LocationTypeType } from 'common/models/internal/MasterLocation';
import { getClientDimensionsMapping } from 'common/redux/selectors/clientSelector';
import {
    lineItem as lineItemApi,
    skill as skillsApi
} from 'common/middleware/api';
import { FieldTypeEnum } from 'common/enums/FieldTypeEnum';
import { Skill } from 'common/models/internal/Skill';

import LineItemActions from 'common/redux/actions/lineItem';
import FilterListBuilder from 'common/models/helpers/FilterListBuilder';
import LineItemPartsInput from './LineItemPartsInput';
import LineItemCartonInput from './LineItemCartonInput';
import LineItemTemperatureInput from './LineItemTemperatureInput';
import UnitConversionHelper from 'common/helpers/UnitConversionHelper';
import CodeInputWithId from 'common/entity/generic/CodeInputWithId';
import lineItemMasterIDs from 'common/tests/masterManagement/lineItemMaster.json';
import { ENTITY_NAME_ID } from 'common/constants/entity/lineItemMaster';

// Filter for EntityLocationCodeInput field
const locationCodeInputFilters = ({ input }) => {
    let filters = new FilterListBuilder()
        .add(
            new FilterBuilder()
                .withName('status')
                .withOperation(FilterOperation.Equals)
                .withValue('ACTIVE')
        )
        .add(
            new FilterBuilder()
                .withName('type')
                .withOperation(FilterOperation.Equals)
                .withValue(LocationTypeType.ManufacturingPlant)
        );
    if (input) {
        filters = filters.add(
            new FilterBuilder()
                .withName('name')
                .withOperation(FilterOperation.StartsWith)
                .withValue(input)
        );
    }
    return filters.build();
};

const LINEITEM_FIELDS = [
    {
        accessor: 'code',
        label: 'skuForm.code',
        testId: lineItemMasterIDs.addNewLineItem.inputCode,
        helpText: 'skuForm.codeTooltip',
        placeholder: 'skuForm.codePlaceholder',
        disabledOnEdit: true,
        required: true,
        type: FieldTypeEnum.Text,
        component: CodeInputWithId,
        extraProps: ({ entity, field, props }) => {
            const {
                validateDuplicateId,
                onValidateDuplicateIdChange,
                clientId
            } = props;
            return {
                validateDuplicateId,
                onValidateDuplicateIdChange,
                id: entity.getIn(['id']),
                code: entity.getIn(field.absoluteAccessor),
                clientId
            };
        },
        onChangeHandler: ({ entity, value }) => {
            const { id, code } = value;
            if ('id' in value) {
                return entity.setId(id);
            } else if ('code' in value) {
                return entity.setCode(code);
            }
        }
    },
    {
        accessor: 'name',
        label: 'skuForm.name',
        testId: lineItemMasterIDs.addNewLineItem.inputName,
        placeholder: 'skuForm.namePlaceholder',
        required: true,
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'description',
        label: 'skuForm.description',
        placeholder: 'skuForm.descriptionPlaceholder',
        testId: lineItemMasterIDs.addNewLineItem.inputDescription,
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'scanId',
        label: 'skuForm.scanId',
        placeholder: 'skuForm.scanIdPlaceholder',
        testId: lineItemMasterIDs.addNewLineItem.inputScanId,
        type: FieldTypeEnum.Text
    },
    {
        accessor: 'category',
        label: 'skuForm.category',
        helpText: 'skuForm.categoryTooltip',
        placeholder: 'skuForm.categoryPlaceholder',
        testId: lineItemMasterIDs.addNewLineItem.inputCategory,
        type: FieldTypeEnum.Select,
        options: ({ props }) => {
            const { entitySettings } = props;
            return entitySettings?.lineItemSettings.getOptionsForSelect() || [];
        },
        onChangeHandler: ({ field, entity, value }) =>
            entity.setIn(field.absoluteAccessor, value?.value || null)
    },
    {
        accessor: 'parts',
        dependentAccessors: [['carton']],
        label: 'skuForm.skuMeasurements',
        component: LineItemPartsInput,
        extraProps: ({ field, entity, props, t }) => {
            return {
                t,
                translationsObject: {
                    weightValidationError:
                        'skuValidationErrors.weightNotANumber',
                    rowLabel: 'skuForm.sectionPart',
                    volumeLabel: 'skuForm.volume',
                    weightLabel: 'skuForm.weight',
                    volumeTitle: 'skuForm.partVolumeType',
                    volumeHelpText: 'skuForm.partVolumeHelpText',
                    addAnotherPart: 'skuForm.addAnotherPart',
                    addPart: 'skuForm.addPart',
                    addPartTooltip: 'skuForm.addAnotherPartTooltip'
                },
                unitConverter: props.unitConverter,
                parts: entity.getIn(field.absoluteAccessor),

                weightTestId:
                    lineItemMasterIDs.addNewLineItem.inputMeasurements
                        .inputWeight,
                volumeTestId: {
                    lengthTestId:
                        lineItemMasterIDs.addNewLineItem.inputMeasurements
                            .inputLength,
                    heightTestId:
                        lineItemMasterIDs.addNewLineItem.inputMeasurements
                            .inputHeight,
                    breadthTestId:
                        lineItemMasterIDs.addNewLineItem.inputMeasurements
                            .inputBreadth
                }
            };
        }
    },
    {
        accessor: 'transactionDuration',
        label: 'skuForm.transactionDuration',
        helpText: 'skuForm.transactionDurationTooltip',
        testId: lineItemMasterIDs.addNewLineItem.inputDuration,
        halfWidth: true,
        type: FieldTypeEnum.TransactionDuration,
        placeholder: 'skuForm.transactionDurationPlaceholder',
        onChangeHandler: ({ field, entity, value }) =>
            entity.setIn(field.absoluteAccessor, value * 60)
    },
    {
        accessor: 'instructions',
        label: 'skuForm.instructionsLabel',
        type: FieldTypeEnum.Notes,
        placeholder: 'skuForm.instructionsPlaceholder',
        extraProps: () => {
            return {
                translationsObject: {
                    rowLabel: 'skuForm.noteCount',
                    addBtnText: 'skuForm.addInstructions'
                },
                addBtnTestId:
                    lineItemMasterIDs.addNewLineItem.inputNotes.addButton,
                addTextId:
                    lineItemMasterIDs.addNewLineItem.inputNotes.addNotesText
            };
        }
    },
    {
        accessor: 'carton',
        label: 'skuForm.boxDetails',
        helpText: 'skuForm.boxDetailsTooltip',
        dependentAccessors: [['parts']],
        component: LineItemCartonInput,
        extraProps: ({ entity, field, props, t }) => {
            return {
                t,
                hiddenModelProperties: props.entitySettings.getHiddenModelProperties(
                    'lineItemSettings'
                ),
                translationsObject: {
                    addBtnText: 'skuForm.addDetails',
                    volumeLabel: 'skuForm.volume',
                    weightLabel: 'skuForm.weight'
                },
                lineItem: entity,
                unitConverter: props.unitConverter,
                carton: entity.getIn(field.absoluteAccessor)
            };
        }
    },
    {
        accessor: 'shouldCrate',
        label: 'skuForm.isCrateable',
        helpText: 'skuForm.isLineItemCratableTooltip',
        type: FieldTypeEnum.Boolean,
        testId: lineItemMasterIDs.addNewLineItem.creatableToggle
    },
    {
        accessor: 'temperatureThreshold',
        label: 'skuForm.temperatureThreshold',
        helpText: 'skuForm.temperatureThresholdTooltip',
        component: LineItemTemperatureInput,
        extraProps: ({ entity, field, t, props }) => {
            return {
                t,
                translationsObject: {
                    minTempLabel: 'skuForm.minTempLabel',
                    maxTempLabel: 'skuForm.maxTempLabel',
                    tempTypeLabel: 'skuForm.tempType',
                    tempTypePlaceholder: 'skuForm.tempTypePlaceholder',
                    tempOptionLabel: 'skuProperties.temperatureTypes'
                },
                unitConverter: props.unitConverter,
                hiddenModelProperties: props.entitySettings.getHiddenModelProperties(
                    'lineItemSettings'
                ),
                temperatureThreshold: entity.getIn(field.absoluteAccessor),
                minTempTestId:
                    lineItemMasterIDs.addNewLineItem.inputTemperature
                        .inputMinTemp,
                maxTempTestId:
                    lineItemMasterIDs.addNewLineItem.inputTemperature
                        .inputMaxTemp,
                refigerationTypeTestId:
                    lineItemMasterIDs.addNewLineItem.inputTemperature
                        .inputRefigerationType
            };
        }
    },
    {
        accessor: 'skills',
        type: FieldTypeEnum.Skills,
        options: ({ field }) => {
            const skills = field.fetchInfo.response?.skills || [];
            return skills && skills.map(skill => Skill.fromJS(skill));
        },
        extraProps: ({ field, t }) => {
            return {
                translationsObject: {
                    label: t('skuForm.skills'),
                    placeholder: t('skuForm.skillSelectPlaceholder'),
                    addSkillsText: t('skuForm.addSkill')
                },
                isLoading: !field.fetchInfo.response,
                testId: lineItemMasterIDs.addNewLineItem.selectLineItemSkills
            };
        },
        fetchInfo: {
            fetcher: skillsApi.fetch.bind(skillsApi),
            params: ({ props }) => {
                return { clientId: props.clientId };
            },
            response: null
        }
    },
    {
        accessor: 'locations',
        label: 'skuForm.manufacturingLocations',
        helpText: 'skuForm.manufacturingLocationTooltip',
        type: FieldTypeEnum.LocationCodeInput,
        placeholder: 'locationForm.locationPlaceholder',
        extraProps: ({ entity }) => {
            return {
                isMulti: true,
                doubleLine: false,
                closeMenuOnSelect: false,
                creatable: false,
                getLocationFilters: locationCodeInputFilters,
                selectedLocations: entity.locations.toArray(),
                getOptionLabel: option => option.name,
                getOptionMeta: () => null,
                testId:
                    lineItemMasterIDs.addNewLineItem.selectManufacturingLocation
            };
        }
    },
    {
        accessor: 'customProperties',
        type: FieldTypeEnum.CustomFields,
        extraProps: () => {
            return {
                settingsName: 'lineItemSettings'
            };
        }
    }
];

const processFetchedEntity = ({ entity, props }) => {
    const {
        specificProps: { unitConverter }
    } = props;
    const { parts } = entity;
    const updatedParts = parts.map(part =>
        part.convertToPreferredUnit(unitConverter)
    );
    return entity.setParts(updatedParts);
};

const LINE_ITEM_INITIAL_FETCH_FIELDS = ['skills'];

const SingleLineItemEditContainer = entityMasterSingleEditFactory({
    MasterEntity: MasterLineItem,
    MasterActions: LineItemActions,
    EntityFetchAPI: lineItemApi,
    extraApiParams: {
        include: 'LOCATIONS'
    },
    INITIAL_FETCH_FIELDS: LINE_ITEM_INITIAL_FETCH_FIELDS,
    FIELDS: LINEITEM_FIELDS,
    options: {
        ENTITY_NAME_ID,
        PARAM_ID: 'lineItemId',
        ENTITY_SETTINGS_NAME: 'lineItemSettings'
    },
    processFetchedEntity,
    mapEntityStateToProps: ({ state, props }) => {
        const dimensionsMapping = getClientDimensionsMapping(state, props);

        let unitConverter = null;
        if (dimensionsMapping) {
            unitConverter = new UnitConversionHelper({
                dimensionsMapping
            });
        }

        return {
            unitConverter
        };
    }
});

export default SingleLineItemEditContainer;
